import { useReducer, useEffect } from "react";

import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  Select,
  Modal,
  Input,
  TextArea
} from "@theorgchartdev/storybook";
import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import { appstr } from "@coreapp/utils/appstr";
import { ListItemOption } from "@theorgchartdev/storybook/dist/components/List/ListItem";
import {
  PlanObjectiveColors,
  ObjectiveHandler,
  planObjectiveHelpLink,
  PlanObjectives,
  DefaultPlanObjective
} from "@/Utils/Plan/ObjectivesHandler";
import { CapitalizeFirstWord } from "@/Utils/DataLib";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { useRenameDocController } from "@/hooks/useRenameDocController";

type DocDetailsFormState = {
  name: string;
  description: string;
  objective?: Nullable<PlanObjectives>;
  isDirty: boolean;
};

type Action = {
  type: string;
  payload: any;
};

type DocDetailsEditModalProps = {
  show: boolean;
  onClose: () => void;
  initialData: {
    name: string;
    description: string;
    objective?: Nullable<PlanObjectives>;
  };
  mode: "layouteditor" | "report" | "plan" | "baseChart" | "custom";
};

const docDetailsFormReducer = (state: DocDetailsFormState, action: Action) => {
  const dirtyState = { ...state, isDirty: true };

  switch (action.type) {
    case "SET_NAME":
      return { ...dirtyState, name: action.payload };
    case "SET_DESCRIPTION":
      return { ...dirtyState, description: action.payload };
    case "SET_OBJECTIVE":
      return { ...dirtyState, objective: action.payload };
    case "RESET":
      return {
        ...dirtyState,
        isDirty: false
      };
    default:
      return state;
  }
};

const DocDetailsEditModal = (props: DocDetailsEditModalProps) => {
  const { show, onClose, mode } = props;

  if (!show || ["report", "layouteditor"].includes(mode)) {
    return null;
  }

  const { SaveSettings, errorMsg } = useRenameDocController();

  const docMgr = GetDoc()?.GetDocMgr();
  const iWiz = docMgr?.GetIWizDataRefresh();

  const isPlan = mode === "plan";
  let entityStringRaw = isPlan
    ? appstr("Plan")
    : mode === "baseChart"
      ? appstr("Base Chart")
      : appstr("Custom Chart");
  const entityString = CapitalizeFirstWord(entityStringRaw.toLowerCase());

  // Form state
  const [formState, dispatch] = useReducer(docDetailsFormReducer, {
    name: "",
    description: "",
    objective: null,
    isDirty: false
  });

  useEffect(() => {
    let description, objective;
    const name = docMgr?.GetFormattedChartName(false, false);

    switch (mode) {
      case "baseChart":
        description = iWiz?.GetDescription();
        break;
      case "custom":
        description = iWiz?.GetDescription();
        break;
      case "plan":
        const planInfo = GetWSMgr()?.GetPlanMgr()?.GetPlanInfo();
        description = planInfo?.description;
        objective = planInfo?.objective || DefaultPlanObjective;
        break;
      default:
        return;
    }

    dispatch({ type: "SET_NAME", payload: name ?? "" });
    dispatch({ type: "SET_DESCRIPTION", payload: description ?? "" });
    if (isPlan) dispatch({ type: "SET_OBJECTIVE", payload: objective ?? DefaultPlanObjective });
    dispatch({ type: "RESET", payload: null });
  }, []);

  const handleClose = () => {
    onClose();
  };

  const saveChanges = async () => {
    const { name, description, objective } = formState;
    const details: Record<string, any> = { input: name, desc: description };
    if (isPlan) details.objective = objective ?? DefaultPlanObjective;

    const saved = await SaveSettings(details);
    if (saved) handleClose();
  };

  const isFormValid = () => {
    return formState.name.length > 0 && (!isPlan || formState.objective !== null);
  };

  const baseClass = "ocn-builder-desc";
  const objectiveOptions = isPlan
    ? ObjectiveHandler.buildObjectiveOptions((objective: ListItemOption) =>
        dispatch({ type: "SET_OBJECTIVE", payload: objective.value })
      )
    : [];
  const requiredTicker = <span className={"ocn-form-label-required"}> *</span>;

  return (
    <CentralizedModalContainer show={show}>
      <Modal
        id="builder-card"
        show={show}
        centerScreen={true}
        onClose={handleClose}
        fullWidth={true}
        customStyle={{ padding: 0, width: "600px" }}
        className="ocn-details-edit-modal"
      >
        <DialogTitle divider={true} onClose={handleClose}>
          <span className={"ocn-explorer-icon-label"}>
            <Icon size={"medium"} name={"pen-to-square"} />
            {appstr("Edit entity details", { entity: entityString.toLowerCase() })}
          </span>
        </DialogTitle>

        {/* NAME */}
        <DialogContent dividers={false}>
          <Input
            id={"builder-name-input"}
            dataTestId={"builder-name-input"}
            key={"name-plan-input"}
            label={appstr("Entity name", { entity: entityString })}
            placeholder={appstr("Entity name", { entity: entityString })}
            value={formState.name}
            size={"medium"}
            status={errorMsg ? "error" : "default"}
            message={errorMsg || undefined}
            onChange={e =>
              dispatch({ type: "SET_NAME", payload: (e.target as HTMLInputElement).value })
            }
            require
          />
        </DialogContent>

        {/* DESCRIPTION */}
        <DialogContent dividers={false}>
          <TextArea
            id={"builder-description-input"}
            dataTestId={"builder-description-input"}
            key={"description-plan-input"}
            label={appstr("Description")}
            placeholder={appstr("Add a description")}
            value={formState.description}
            charLimit={1000}
            onChange={e =>
              dispatch({
                type: "SET_DESCRIPTION",
                payload: (e.target as HTMLTextAreaElement).value
              })
            }
          />
        </DialogContent>

        {/* PLAN OBJECTIVE */}
        {isPlan && (
          <DialogContent dividers={false}>
            <span className={baseClass}>
              <label
                className="ocn-form-label ocn-form-input-label-vertical-gap"
                htmlFor="doc-details-objective-select"
              >
                {appstr("Plan Objective")}
                {requiredTicker}
              </label>
              <div className={"ocn-builder-search"}>
                <span className={"ocn-builder-edit-text"}>
                  <Select
                    id="doc-details-objective-select"
                    dataTestId="builder-objective-menu"
                    optionSize="medium"
                    selectSize="medium"
                    placeholder={formState.objective ? "" : appstr("Select objective")}
                    value={formState.objective}
                    options={objectiveOptions}
                    addPill
                    getPillColor={value => PlanObjectiveColors[value]}
                    onRemoveOption={(_, e) => {
                      e.stopPropagation();
                      dispatch({ type: "SET_OBJECTIVE", payload: null });
                    }}
                  />
                </span>
              </div>
              <span className={"ocn-builder-help"}>
                {appstr("Plan Objective Help") + " "}
                <a href={planObjectiveHelpLink} target="_blank" rel="noreferrer">
                  {appstr("Plan Objective Help Link")}
                </a>
                .
              </span>
            </span>
          </DialogContent>
        )}

        {/* ACTIONS */}
        <DialogActions divider={true} size="medium">
          <DialogActions.Tertiary
            onClick={handleClose}
            size="medium"
            dataTestId={"builder-cancel-btn"}
          >
            {appstr("Cancel")}
          </DialogActions.Tertiary>
          <DialogActions.Primary
            onClick={saveChanges}
            dataTestId={"builder-create-btn"}
            size="medium"
            disabled={!formState.isDirty || !isFormValid()}
          >
            {appstr("Save")}
          </DialogActions.Primary>
        </DialogActions>
      </Modal>
    </CentralizedModalContainer>
  );
};

export default DocDetailsEditModal;
